import React, { useEffect, useState } from 'react';
import { Container, Row, Col, ListGroup, Card, Input, Button } from 'reactstrap';
import { format, isToday, isYesterday, startOfDay, isEqual } from 'date-fns';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';

import { socket } from '../../config/Socket';
import PreviewModal from '../Chat/PreviewModal';
import AttachmentModal from '../Chat/AttachmentModal';
import { getFileExtension } from '../../utils/common';
import FormSelect from '../../components/form/FormSelect';
import gmailIcon from '../../assets/images/icons/gmail.svg';
import FormTextarea from '../../components/form/FormTextarea';
import AddEditMetaNumberModal from './AddEditMetaNumberModal';
import SendTemplateModal from './SendTemplateModal';
import whatsappIcon from '../../assets/images/icons/whatsapp.svg';

import {
  addMetaHelpNote,
  assignMetaListGet,
  assignToAgentMeta,
  getMetaHelpNote,
  metaHelpHistoryGet,
  metaHelpMarkAsRead,
  sendMetaHelpMsg,
  setActiveMetaHelpDetails,
  unassignMetaListGet,
  unreadMetaCountGet,
  useActiveMetaHelpDetails,
  useAssignMetaList,
  useHelpMetaCountList,
  useUnassignMetaList
} from '../../store/slices/tenantSlices/metaHelpDeskSlice';
import { useUserDetails } from '../../store/slices/authSlice';
import { userOptionGetByProcessId } from '../../store/slices/callingSlice';

const MetaHelpdesk = () => {
  const dispatch = useDispatch();
  const user = useUserDetails();
  const { t } = useTranslation();

  const helpMetaCountList = useHelpMetaCountList();
  const activeMetaHelpDetails = useActiveMetaHelpDetails();

  const assignMetaList = useAssignMetaList();
  const unassignMetaList = useUnassignMetaList();

  const [activeTab, setActiveTab] = useState('mine');
  const [chatHistory, setChatHistory] = useState([]);
  const [attachModal, setAttachModal] = useState(false);
  const [templateModal, setTemplateModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditCrm, setIsEditCrm] = useState({ isOpen: false, ticketId: '', name: '' });
  const [isAddCrm, setIsAddCrm] = useState(false);
  const [isAssign, setIsAssign] = useState(false);
  const [isPreviewUrl, setIsPreviewUrl] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [allUserOptions, setAllUserOptions] = useState([]);
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [notesData, setNotesData] = useState([]);
  const [assignToAgentDetails, setAssignToAgentDetails] = useState({
    ticketId: '',
    agentId: '',
    status: '',
    priority: ''
  });
  const [isInfo, setIsInfo] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    setActiveTab(activeMetaHelpDetails?.activeTab || activeTab);
  }, [user, activeMetaHelpDetails]);

  const fetchUnassignMetaList = async () => {
    try {
      await dispatch(unassignMetaListGet()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchAssignList = async () => {
    try {
      await dispatch(assignMetaListGet()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchChatHistory = useDebouncedCallback(
    async (activeMetaHelpDetails) => {
      try {
        const payload = {
          ticketId: activeMetaHelpDetails?.id,
          page: 1,
          limit: 100,
          search: search || undefined
        };
        const response = await dispatch(metaHelpHistoryGet(payload)).unwrap();
        setChatHistory(response?.data?.reverse() || []);
      } catch (error) {
        console.log('error', error);
      }
    },
    search ? 0 : 500
  );

  useEffect(() => {
    if (activeMetaHelpDetails) {
      if (activeTab === 'unassigned') {
        setIsAssign(true);
      } else {
        setIsAssign(false);
      }
      fetchChatHistory(activeMetaHelpDetails);
      setAssignToAgentDetails({
        ...assignToAgentDetails,
        ticketId: activeMetaHelpDetails.id,
        status: activeMetaHelpDetails?.status,
        priority: activeMetaHelpDetails?.priority,
        agentId: activeMetaHelpDetails?.assignedToId
      });
    }
  }, [activeMetaHelpDetails, search]);

  useEffect(() => {
    fetchAssignList();
    fetchUnassignMetaList();
  }, [activeTab]);

  const handleSend = async (file = undefined) => {
    if ((file ? false : !message?.trim()) || isLoading) return false;
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('to', activeMetaHelpDetails?.from_whatsapp_number);
      formData.append('from', activeMetaHelpDetails?.to_whatsapp_number);
      formData.append('message', message);
      formData.append('type', file ? 'media' : 'text');
      formData.append('direction', 'outbound');

      if (file) {
        formData.append('file', file);
      }

      await dispatch(sendMetaHelpMsg(formData)).unwrap();
      fetchChatHistory(activeMetaHelpDetails);
      setMessage('');
      setAttachModal(false);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('error', error);
    }
  };

  const onAssignToAgent = async (action = '', new_type = '', old_type = '', agentId = '') => {
    try {
      const payload = {
        ticketId: activeMetaHelpDetails.id,
        agentId: agentId || assignToAgentDetails?.agentId,
        status: action === 'Type' ? new_type : assignToAgentDetails?.status,
        priority: action === 'Status' ? new_type : assignToAgentDetails?.priority,
        action,
        new_type,
        old_type
      };
      await dispatch(assignToAgentMeta(payload)).unwrap();
      fetchChatHistory(activeMetaHelpDetails);
      fetchAssignList();
      fetchUnassignMetaList();
      if (
        (action === 'transformed' && old_type === 'system') ||
        (action === 'Type' && new_type === 'Close')
      ) {
        dispatch(setActiveMetaHelpDetails(null));
        setIsInfo(false);
        setActiveTab('mine');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const groupDataByRegister = (data) => {
    if (!data) return [];

    const createOptionsFromData = (filteredData) =>
      filteredData.map((item) => ({
        label: item.Username,
        value: item.id
      }));

    const registeredData = data.filter((item) => item.register === 'yes');
    const unregisteredData = data.filter((item) => item.register === 'no');

    return [
      {
        label: 'Registered',
        options: createOptionsFromData(registeredData)
      },
      {
        label: 'Unregistered',
        options: createOptionsFromData(unregisteredData)
      }
    ];
  };

  const fetchUserOptionByProcessId = async () => {
    try {
      const response = await dispatch(
        userOptionGetByProcessId({ process_id: user?.process_id })
      ).unwrap();
      setUserOptions(groupDataByRegister(response?.data));
      setAllUserOptions(response?.data);
    } catch (error) {
      console.error('Error fetching user options:', error);
    }
  };

  useEffect(() => {
    if (user?.process_id) {
      fetchUserOptionByProcessId();
    }
  }, [user?.process_id]);

  // useEffect(() => {
  //   dispatch(setActiveMetaHelpDetails(null));
  //   setIsInfo(false);
  // }, [activeTab]);

  const tabs = [
    { label: t('mine'), key: 'mine' },
    { label: t('unassigned'), key: 'unassigned' }
  ];

  const visibleTabs = tabs.filter((tab) => tab.condition).length;
  const tabWidth = visibleTabs === 2 ? 'w-50' : 'w-100';

  const getFileName = (path) => {
    const pathnames = path?.split('/');
    return pathnames?.[pathnames.length - 1];
  };

  const fileDownload = async (url) => {
    try {
      const response = await fetch(url, {
        mode: 'cors' // or 'no-cors' if needed — but only if you're not accessing the response body
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;

      // Get filename from URL
      const filename = url.split('/').pop();
      link.download = filename || 'download';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revoke the object URL after use to free up memory
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const fetchMetaHelpChatCount = async () => {
    try {
      const payload = {
        to: user?.to_meta_whatsapp_number
      };
      await dispatch(unreadMetaCountGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const onMarkAsReadHelp = async (from, to) => {
    try {
      const payload = {
        from: from,
        to: to
      };
      await dispatch(metaHelpMarkAsRead(payload)).unwrap();
      fetchMetaHelpChatCount();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    const handleChatMessage = (message) => {
      console.log('message------->', message);
      if (message?._id === activeMetaHelpDetails?._id) {
        onMarkAsReadHelp(message?.from_whatsapp_number, message?.to_whatsapp_number);
        fetchChatHistory({ id: message?._id, activeTab: activeTab });
      } else {
        fetchMetaHelpChatCount();
      }
    };

    socket.on('received-whatsapp-meta-message', handleChatMessage);

    return () => {
      socket.off('received-whatsapp-meta-message', handleChatMessage);
    };
  }, [activeTab, activeMetaHelpDetails]);

  useEffect(() => {
    const handleChatMessage = (message) => {
      console.log('message------->', message);
      if (message?._id === activeMetaHelpDetails?._id) {
        fetchChatHistory({ id: message?._id, activeTab: activeTab });
      }
    };

    socket.on('unassign-whatsapp-meta-notify', handleChatMessage);

    return () => {
      socket.off('unassign-whatsapp-meta-notify', handleChatMessage);
    };
  }, [activeTab, activeMetaHelpDetails]);

  const findUserName = (id) => {
    const data = allUserOptions?.find((val) => val?.id === Number(id));
    return data?.Username;
  };

  const getNote = async () => {
    try {
      const payload = {
        ticketId: activeMetaHelpDetails.id
      };
      const response = await dispatch(getMetaHelpNote(payload)).unwrap();
      setNotesData(response?.data || []);
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (activeMetaHelpDetails) {
      getNote();
    }
  }, [activeMetaHelpDetails]);

  const submitNote = async () => {
    try {
      const payload = {
        ticketId: assignToAgentDetails?.ticketId,
        comment: notes,
        noteType: 'internal'
      };

      await dispatch(addMetaHelpNote(payload)).unwrap();
      getNote();
      setNotes('');
    } catch (error) {
      console.log('error', error);
    }
  };

  return (
    <Container fluid className="chat-app">
      <Row className="h-100">
        <Col md="3" className="chat-app__sidebar">
          <div className="d-flex align-items-center">
            {tabs.map((tab) => (
              <span
                key={tab.key}
                className={`text-capitalize d-flex gap-2 justify-content-center text-center p-2 cursor-pointer ${tabWidth} ${activeTab === tab.key ? 'border-bottom border-1 border-white fw-bolder text-white' : 'text-lightGray'}`}
                onClick={() => {
                  setActiveTab(tab.key);
                  setIsInfo(false);
                  dispatch(setActiveMetaHelpDetails(null));
                }}>
                {tab.label}{' '}
                {tab.key === 'mine' && helpMetaCountList?.length > 0 && (
                  <label className="bg-info rounded-circle msg-badge">
                    {helpMetaCountList?.length}
                  </label>
                )}
              </span>
            ))}
          </div>
          <ListGroup flush>
            {(activeTab === 'mine' ? assignMetaList : unassignMetaList).map((val, index) => {
              const countObj =
                helpMetaCountList?.find(
                  (x) => x?._id?.from_whatsapp_number === val?.from_whatsapp_number
                ) || null;
              return (
                <div
                  key={index}
                  className={`chat-user p-2 ${activeMetaHelpDetails?.id === val?.id && activeMetaHelpDetails?.name === val?.name ? 'active-chat-user' : ''}`}
                  onClick={() => {
                    if (countObj) {
                      onMarkAsReadHelp(
                        countObj?._id?.from_whatsapp_number,
                        val?.to_whatsapp_number
                      );
                    }
                    dispatch(setActiveMetaHelpDetails({ ...val, activeTab: activeTab }));
                  }}>
                  <img
                    src={val?.category === 'Whatsapp' ? whatsappIcon : gmailIcon}
                    width={25}
                    height={25}
                  />
                  <div
                    className="d-flex justify-content-between align-items-center w-75"
                    style={{ maxWidth: '70%' }}>
                    <label
                      style={{
                        // width: '85%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                      {val?.name}
                    </label>
                  </div>
                  {countObj && (
                    <div className="d-flex justify-content-end w-25" style={{ maxWidth: '20%' }}>
                      <label className="bg-info rounded-circle msg-badge">{countObj?.count}</label>
                    </div>
                  )}
                </div>
              );
            })}
          </ListGroup>
          <Button className="w-100" onClick={() => setIsAddCrm(true)}>
            {t('startConversation')}
          </Button>
        </Col>

        {activeMetaHelpDetails ? (
          <Col md={isInfo ? '6' : '9'} className="chat-app__main d-flex flex-column">
            <div className="chat-app__header d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <span>
                  <img
                    src={activeMetaHelpDetails?.category === 'Whatsapp' ? whatsappIcon : gmailIcon}
                    width={28}
                    height={28}
                  />
                </span>
                <strong className="text-capitalize ms-2">{activeMetaHelpDetails.name}</strong>
              </div>
              <div className="d-flex align-items-center gap-3">
                <Input
                  className="form-control bg-transparent text-white custom-placeholder"
                  name="search"
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value || '')}
                />
                <Icon.Users
                  size={20}
                  className="cursor-pointer"
                  onClick={() => setIsAssign(!isAssign)}
                />
                <Icon.Info
                  size={20}
                  className="cursor-pointer"
                  onClick={() => setIsInfo(!isInfo)}
                />
              </div>
            </div>
            {isAssign ? (
              <div className="chat-app__header2">
                <Row>
                  <Col lg="4" md="4">
                    <FormSelect
                      className="select-my-2"
                      isShowLabel={false}
                      name="agentId"
                      placeholder="Transfer Agent"
                      value={assignToAgentDetails?.agentId}
                      options={userOptions}
                      onChange={(e) => {
                        setAssignToAgentDetails({
                          ...assignToAgentDetails,
                          agentId: e?.target?.value
                        });
                        onAssignToAgent(
                          'transformed',
                          e?.target?.label,
                          activeMetaHelpDetails?.assignedToId || 'system',
                          e?.target?.value
                        );
                      }}
                      isGroup
                    />
                  </Col>
                  <Col lg="4" md="4">
                    <FormSelect
                      className="select-my-2"
                      isShowLabel={false}
                      name="priority"
                      placeholder="Select priority"
                      value={assignToAgentDetails?.priority}
                      disabled={!assignToAgentDetails?.agentId}
                      options={[
                        { label: 'High', value: 'High' },
                        { label: 'Medium', value: 'Medium' },
                        { label: 'Low', value: 'Low' }
                      ]}
                      onChange={(e) => {
                        setAssignToAgentDetails({
                          ...assignToAgentDetails,
                          priority: e?.target?.value
                        });
                        onAssignToAgent(
                          'Status',
                          e?.target?.value,
                          activeMetaHelpDetails?.priority
                        );
                      }}
                    />
                  </Col>
                  <Col lg="4" md="4">
                    <FormSelect
                      className="select-my-2"
                      isShowLabel={false}
                      name="status"
                      placeholder="Select Status"
                      disabled={!assignToAgentDetails?.agentId}
                      value={assignToAgentDetails?.status}
                      options={[
                        { label: 'Open', value: 'Open' },
                        { label: 'In Progress', value: 'In Progress' },
                        { label: 'Close', value: 'Close' }
                      ]}
                      onChange={(e) => {
                        setAssignToAgentDetails({
                          ...assignToAgentDetails,
                          status: e?.target?.value
                        });
                        onAssignToAgent('Type', e?.target?.value, activeMetaHelpDetails?.status);
                      }}
                    />
                  </Col>
                </Row>
              </div>
            ) : null}
            <div
              className="chat-app__messages flex-grow-1 p-3 overflow-auto"
              style={{
                ...(activeTab === 'unassigned' && {
                  maxHeight: isAssign ? 'calc(100vh - 210px)' : 'calc(100vh - 160px)'
                }),
                ...(activeTab === 'mine' && {
                  maxHeight: isAssign ? 'calc(100vh - 260px)' : 'calc(100vh - 210px)'
                })
              }}>
              {chatHistory.map((msg, idx, array) => {
                const isSentByCurrentUser = msg?.direction !== 'inbound';
                const formattedTime = format(new Date(msg.timestamp), 'hh:mm a');
                const extension = getFileExtension(msg?.media?.url || null);

                const currentDate = startOfDay(new Date(msg.timestamp));
                const nextDate = startOfDay(new Date(chatHistory[idx + 1]?.timestamp)) || null;

                const isNewDay = array?.length - 1 === idx ? true : !isEqual(currentDate, nextDate);

                let dateLabel = '';
                if (isNewDay) {
                  if (isToday(currentDate)) {
                    dateLabel = 'Today';
                  } else if (isYesterday(currentDate)) {
                    dateLabel = 'Yesterday';
                  } else {
                    dateLabel = format(currentDate, 'dd MMMM yyyy');
                  }
                }

                if (['transformed', 'Type', 'Status', 'assign'].includes(msg?.action)) {
                  return (
                    <React.Fragment key={msg._id}>
                      <div className="d-flex justify-content-center align-items-center">
                        {msg?.action === 'transformed' || msg?.action === 'assign' ? (
                          <span className="assign-notification">
                            {`Ticket transformed${msg?.old_type !== 'system' ? ` from ${findUserName(msg?.old_type)}` : ` from ${msg?.old_type}`} to ${msg?.new_type}`}
                          </span>
                        ) : msg?.action === 'Type' ? (
                          <span className="assign-notification">
                            {`Ticket Status changed ${msg?.old_type} to ${msg?.new_type}`}{' '}
                          </span>
                        ) : (
                          <span className="assign-notification">
                            {`Ticket Type changed ${msg?.old_type} to ${msg?.new_type}`}{' '}
                          </span>
                        )}
                      </div>
                      {dateLabel && (
                        <div className="d-flex justify-content-center align-items-center">
                          <span className="assign-notification" style={{ padding: '5px 20px' }}>
                            {dateLabel}
                          </span>
                        </div>
                      )}
                    </React.Fragment>
                  );
                }
                return (
                  <React.Fragment key={msg._id}>
                    <div
                      className={`chat-app__message-wrapper ${isSentByCurrentUser ? 'sent' : 'received'}`}>
                      <div
                        className={`chat-app__message ${msg?.media?.url ? 'chat-app__media' : ''}`}>
                        {msg?.media ? (
                          <React.Fragment>
                            <div className="chat-app__message-text">
                              {[
                                'jpg',
                                'jpeg',
                                'png',
                              'gif',
                              'bmp',
                              'webp',
                              'svg',
                              'ico',
                              'tif',
                              'tiff',
                              'avif'
                            ]?.includes(extension) ? (
                              <div>
                                {msg?.fileName ? msg?.fileName : getFileName(msg?.media?.url)}
                                <div className="image-wrapper">
                                  <img src={msg?.media?.url} />
                                </div>
                                <div className="w-100 d-flex align-items-center gap-1">
                                  <div className="w-50">
                                    <Button
                                      className="w-100"
                                      style={{ fontSize: '12px' }}
                                      onClick={() => setIsPreviewUrl(msg?.media?.url)}>
                                      {t('open')}
                                    </Button>
                                  </div>
                                  <div className="w-50">
                                    <Button
                                      className="w-100"
                                      style={{ fontSize: '12px' }}
                                      onClick={() => {
                                        fileDownload(msg?.media?.url);
                                      }}>
                                      {t('download')}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div>
                                {msg?.fileName ? msg?.fileName : getFileName(msg?.media?.url)}
                                <div className="media-icon">
                                  <Icon.FileText size={40} />
                                </div>
                                <div className="w-100 d-flex align-items-center gap-1">
                                  {extension === 'pdf' ? (
                                    <div className="w-50">
                                      <Button
                                        className="w-100"
                                        style={{ fontSize: '12px' }}
                                        onClick={() => setIsPreviewUrl(msg?.media?.url)}>
                                        {t('open')}
                                      </Button>
                                    </div>
                                  ) : null}
                                  <div className={extension === 'pdf' ? 'w-50' : 'w-100'}>
                                    <Button
                                      className="w-100"
                                      style={{ fontSize: '12px' }}
                                      onClick={() => {
                                        fileDownload(msg?.media?.url);
                                      }}>
                                      {t('download')}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                            {msg?.message ? (
                              <div
                                className="chat-app__message-text bg-transparent mt-2"
                                dangerouslySetInnerHTML={{
                                  __html: msg.message.replace(/\r?\n/g, '<br />')
                                }}
                              />
                            ) : null}
                          </React.Fragment>
                        ) : (
                          <div
                            className="chat-app__message-text"
                            dangerouslySetInnerHTML={{
                              __html: msg.message.replace(/\r?\n/g, '<br />')
                            }}
                          />
                        )}
                        <div className="chat-app__message-time">{formattedTime}</div>
                      </div>
                    </div>
                    {dateLabel && (
                      <div className="d-flex justify-content-center align-items-center">
                        <span className="assign-notification" style={{ padding: '5px 20px' }}>
                          {dateLabel}
                        </span>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            {activeTab === 'mine' ? (
              <div className="chat-app__input d-flex align-items-center p-2 border-top">
                <div
                  className="ms-2 pe-2 text-primary cursor-pointer"
                  onClick={() => setAttachModal(true)}>
                  <Icon.Paperclip size={20} />
                </div>
                <div
                  className="pe-2 text-primary cursor-pointer"
                  onClick={() => setTemplateModal(true)}>
                  <Icon.FileText size={20} />
                </div>
                <Input
                  type="textarea"
                  placeholder="Type a message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault(); // Prevent newline
                      handleSend();
                    }
                  }}
                  rows={1}
                />
                <div className="ms-2 pe-2 text-info" onClick={() => handleSend()}>
                  <Icon.Send size={20} />
                </div>
              </div>
            ) : null}
          </Col>
        ) : (
          <Col md={isInfo ? '6' : '9'}>
            <Card className="p-0 d-flex justify-content-center align-items-center no-data m-0 h-100">
              {t('startChatting')}
              <Button className="mt-2" onClick={() => setIsAddCrm(true)}>
                {t('startConversation')}
              </Button>
            </Card>
          </Col>
        )}
        {isInfo ? (
          <Col md={isInfo ? '3' : '0'} className="chat-app__main d-flex flex-column">
            <div className="chat-app__header chat-app__info d-flex align-items-center justify-content-between">
              <strong className="text-capitalize ms-2">{t('information')}</strong>
            </div>
            <div className="chat-app__info-message p-3 h-100 d-flex flex-column gap-2">
              <div className="d-flex align-items-baseline gap-3">
                <span className="w-25 text-secondary">{t('name')}</span>
                <span className="fs-6 text-primary w-75 d-flex align-items-center gap-2 justify-content-between">
                  {activeMetaHelpDetails?.name}
                  <Icon.Edit
                    size={15}
                    className="cursor-pointer"
                    onClick={() => {
                      setIsEditCrm({
                        isOpen: true,
                        ticketId: activeMetaHelpDetails?.id,
                        name: activeMetaHelpDetails?.name,
                        from: activeMetaHelpDetails?.from_whatsapp_number
                      });
                    }}
                  />
                </span>
              </div>
              <div className="d-flex align-items-baseline gap-3">
                <span className="w-25 text-secondary">{t('priority')}</span>
                <span className="fs-6 text-primary w-75">{activeMetaHelpDetails?.priority}</span>
              </div>
              <div className="d-flex align-items-baseline gap-3">
                <span className="w-25 text-secondary">{t('status')}</span>
                <span className="fs-6 text-primary w-75">{activeMetaHelpDetails?.status}</span>
              </div>
              <div className="w-80 bg-lightGray my-2" style={{ height: '1px' }} />
              <div className="">
                <FormTextarea
                  inputClassName="bg-transparent"
                  name="type_option"
                  label={t('addInternalComment')}
                  placeholder="Type here"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                />
                <Button className="w-100 bg-primary border-primary" onClick={() => submitNote()}>
                  {t('addComment')}
                </Button>
              </div>
              {notesData?.length > 0 ? (
                <React.Fragment>
                  <div className="d-flex align-items-baseline gap-3 pt-2">
                    <span className="w-25 text-secondary fw-bolder">{t('user')}</span>
                    <span className="fs-6 text-primary w-75 fw-bolder">{t('comment')}</span>
                  </div>
                  <div className="chat-app-notes" style={{ overflowY: 'auto' }}>
                    {notesData?.map((item, index) => {
                      return (
                        <div className="d-flex align-items-baseline gap-3" key={index}>
                          <span className="w-25 text-secondary">{item?.username || '-'}</span>
                          <span
                            className="fs-6 text-primary w-75"
                            style={{ wordBreak: 'break-word' }}>
                            {item?.comment}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </React.Fragment>
              ) : null}
            </div>
          </Col>
        ) : null}
      </Row>
      {attachModal && (
        <AttachmentModal
          isOpen={attachModal}
          handleCancel={() => setAttachModal(false)}
          handleSubmit={(file) => handleSend(file)}
          isAllAllowed={true}
          isLoading={isLoading}
        />
      )}
      {templateModal && (
        <SendTemplateModal
          isOpen={templateModal}
          from={user?.to_meta_whatsapp_number || activeMetaHelpDetails?.to_whatsapp_number}
          to={activeMetaHelpDetails?.from_whatsapp_number}
          onCloseClick={(isApiCall = false) => {
            setTemplateModal(false);
            if (isApiCall) {
              fetchChatHistory(activeMetaHelpDetails);
            }
          }}
        />
      )}
      {isPreviewUrl && (
        <PreviewModal
          isOpen={isPreviewUrl}
          url={isPreviewUrl}
          handleCancel={() => setIsPreviewUrl(null)}
        />
      )}
      {(isEditCrm?.isOpen || isAddCrm) && (
        <AddEditMetaNumberModal
          isEdit={isEditCrm?.isOpen || false}
          isOpen={isEditCrm?.isOpen || isAddCrm}
          editRecord={isEditCrm}
          onCloseClick={(isApiCall = false) => {
            setIsEditCrm({ isOpen: false, ticketId: '', name: '' });
            setIsAddCrm(false);
            if (isApiCall) {
              fetchAssignList();
              fetchUnassignMetaList();
            }
          }}
        />
      )}
    </Container>
  );
};

export default MetaHelpdesk;
